/**
 * Merge contract for database row search: every row shard is answered by
 * exactly one source, the local index or the server index, never both. The
 * listed local answer stays as it is; the server only adds shards it left.
 *
 * Pure and framework-free so the rule is testable on its own. The service
 * gathers shard state (indexed, unsynced, versions) and delegates here.
 */
import { decodeStateVector } from 'yjs';

import { base64ToUint8Array } from '../../workspace-engine/utils/base64';

export interface DatabaseSearchLocalInput<Gap> {
  /** Row shards with a hit in the listed local answer. */
  listedShardIds: ReadonlySet<string>;
  /** Row shards the local index holds. */
  indexedShardIds: ReadonlySet<string>;
  /** Row shards with local changes the server has not confirmed. */
  unsyncedShardIds: ReadonlySet<string>;
  /** Databases whose row shards are not all in the local index. */
  gaps: readonly Gap[];
}

export interface DatabaseSearchRemoteInput<RemoteHit> {
  /** Server hits per row shard, with the shard version the server indexed. */
  byShard: ReadonlyMap<
    string,
    { version: string | undefined; hits: readonly RemoteHit[] }
  >;
}

export interface DatabaseSearchMergeResult<RemoteHit, Gap> {
  remoteHits: RemoteHit[];
  /** Databases with row shards no source searched. */
  unsearched: Gap[];
}

/**
 * True when the state vector `a` has seen every update `b` has seen. Both are
 * base64 Yjs state vectors.
 */
export function stateVectorContains(a: string, b: string): boolean {
  const clocks = decodeStateVector(base64ToUint8Array(a));
  for (const [client, clock] of decodeStateVector(base64ToUint8Array(b))) {
    if ((clocks.get(client) ?? 0) < clock) return false;
  }
  return true;
}

/**
 * Picks the server hits of the row shards the local answer leaves to the
 * server. The listed local answer itself never changes.
 *
 * - Without a server answer (`remote` is null: offline or the server search
 *   failed) no server hit counts, and the databases with shards missing from
 *   the local index are reported as unsearched.
 * - A shard with a hit in the listed local answer answers locally: its row
 *   stays where it is listed, and its server hits are dropped so the same row
 *   never shows twice.
 * - The local index answers a shard it holds when the shard has unconfirmed
 *   local changes or its local version is ahead of the version the server
 *   indexed. Server hits of that shard are dropped.
 * - Otherwise the server answers every shard it returned hits for.
 */
export function mergeDatabaseSearchResults<RemoteHit, Gap>(
  local: DatabaseSearchLocalInput<Gap>,
  remote: DatabaseSearchRemoteInput<RemoteHit> | null,
  localVersions: Readonly<Record<string, string>>
): DatabaseSearchMergeResult<RemoteHit, Gap> {
  if (!remote) return { remoteHits: [], unsearched: [...local.gaps] };

  const localAnswers = (shardDocId: string, serverVersion?: string) => {
    if (local.listedShardIds.has(shardDocId)) return true;
    if (!local.indexedShardIds.has(shardDocId)) return false;
    if (local.unsyncedShardIds.has(shardDocId)) return true;
    const localVersion = localVersions[shardDocId];
    return (
      localVersion !== undefined &&
      serverVersion !== undefined &&
      stateVectorContains(localVersion, serverVersion) &&
      !stateVectorContains(serverVersion, localVersion)
    );
  };

  const remoteHits: RemoteHit[] = [];
  for (const [shardDocId, { version, hits }] of remote.byShard) {
    if (!localAnswers(shardDocId, version)) remoteHits.push(...hits);
  }
  return { remoteHits, unsearched: [] };
}
